import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { InputField, SelectSimple } from '@advanza/input'
import { Button, Divider } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import CheckButton from 'components/CheckButton'
import DatePicker from 'components/DatePicker'
import { useModalToast } from 'components/misc/Toaster'
import { getDateNew, SQL_DATE_FORMAT_FNS, utcToLocal } from 'date'
import { addMonths } from 'date-fns'
import { useProvider } from 'hooks/providerHooks'
import useAddFreeLeads from 'hooks/useAddFreeLeads'
import { FreeLeadTypeOption } from 'interfaces/FreeLeads'
import { KeyboardEvent, useEffect, useState } from 'react'
import { getRejectionMessage } from 'utils/getRejectionMessage'

interface Props {
    providerId: number
}

const FreeLeadsModal = NiceModal.create(({ providerId }: Props) => {
    const modal = useModal()
    const provider = useProvider(providerId).provider
    const initialNewEntity = {
        nr_leads: 3,
        expiration: utcToLocal(addMonths(new Date(), 1), SQL_DATE_FORMAT_FNS),
        after_payed: false,
    }
    const { newEntity, onChangeNewEntity, isSaving, onSave, errors } = useAddFreeLeads(
        providerId,
        initialNewEntity,
    )
    const { addModalToast } = useModalToast()
    const [typeOptions, setTypeOptions] = useState<FreeLeadTypeOption[]>([])

    useEffect(() => {
        call('office/free-lead-types/get-manual-list').then((response) =>
            setTypeOptions(response.items ?? []),
        )
    }, [])

    const selectedType = typeOptions.find((option) => option.value === newEntity.free_lead_type)
    const isCommentMissing = !!selectedType?.requires_comment && !(newEntity.comment ?? '').trim()
    const canSave = !isSaving && !!newEntity.free_lead_type && !isCommentMissing

    const closeModal = () => {
        modal.resolve()
        modal.hide()
    }

    const handleSave = () => {
        // `onSave` already put the field messages in `errors`; stay open and name the rest, since
        // nothing was saved and the button is the retry.
        onSave().then(closeModal, (rejection: unknown) =>
            addModalToast({
                id: 'save-error',
                msg: getRejectionMessage(
                    rejection,
                    'Something went wrong while saving the free leads',
                ),
                icon: 'error',
                red: true,
            }),
        )
    }

    // InputField, DatePicker and CheckButton all report which field changed through target.name,
    // but CheckButton hands back a plain object rather than a DOM event.
    const onHandleChange = ({ target }: { target: { name?: string; value: unknown } }) => {
        if (target.name) {
            onChangeNewEntity({ [target.name]: target.value })
        }
    }

    const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && canSave) {
            handleSave()
        }
    }

    return (
        <BaseModal
            closeOnOutSideClick={false}
            preventClose={isSaving}
            mobileFullscreen
            maxWidth={674}>
            <BaseModalHeader>
                <h2 className="text-xl font-bold">Give away free leads</h2>
            </BaseModalHeader>
            <BaseModalMainContent>
                <SelectSimple
                    name="free_lead_type"
                    value={newEntity.free_lead_type || '_hidden_'}
                    legend
                    placeholder="Reason / campaign"
                    hiddenOption="Make a choice..."
                    options={typeOptions.map((option) => ({
                        value: option.value,
                        name: option.label,
                    }))}
                    onChange={(event) => {
                        if (typeof event.target.value === 'string') {
                            onChangeNewEntity({ free_lead_type: event.target.value })
                        }
                    }}
                    error={!!errors?.free_lead_type}
                />
                <Divider m />
                <InputField
                    name="nr_leads"
                    value={newEntity.nr_leads}
                    min={0}
                    max={100}
                    type="number"
                    error={errors?.nr_leads}
                    inputStyle={{ fontWeight: 900, fontSize: 20 }}
                    legend
                    placeholder="Nr of free leads"
                    onKeyDown={onInputKeyDown}
                    onChange={onHandleChange}
                />
                <Divider m />
                <Row>
                    <Col xs={4}>
                        <CheckButton
                            onChange={onHandleChange}
                            value={!newEntity.after_payed}
                            checked={newEntity.after_payed}
                            name="after_payed"
                            msg="require x payed leads"
                        />
                    </Col>
                    <Col xs>
                        {newEntity.after_payed && (
                            <InputField
                                name="enable_after_nr_leads_payed"
                                value={newEntity.enable_after_nr_leads_payed}
                                min={0}
                                max={100}
                                autoFocus
                                error={errors?.enable_after_nr_leads_payed}
                                legend
                                placeholder="nr of payed leads before using free leads"
                                onChange={onHandleChange}
                            />
                        )}
                    </Col>
                </Row>
                {!provider?.isSignedOrFreeTrial && (
                    <>
                        <Divider m />
                        <DatePicker
                            dayPickerProps={{
                                disabled: { before: new Date() },
                                defaultMonth: newEntity.expiration
                                    ? (getDateNew(newEntity.expiration) ?? undefined)
                                    : new Date(),
                            }}
                            onChange={onHandleChange}
                            opened
                            legend
                            placeholder="Expiry date"
                            name="expiration"
                            value={newEntity.expiration || null}
                            readOnlyInputField
                        />
                    </>
                )}
                <Divider m />
                <InputField
                    name="comment"
                    value={newEntity.comment}
                    min={0}
                    max={100}
                    error={
                        errors?.comment ||
                        (isCommentMissing
                            ? { required: `A comment is required for "${selectedType?.label}"` }
                            : undefined)
                    }
                    legend
                    placeholder={
                        selectedType?.requires_comment ? 'Comment (required)' : 'Comment (optional)'
                    }
                    onKeyDown={onInputKeyDown}
                    onChange={onHandleChange}
                />
            </BaseModalMainContent>
            <BaseModalFooter className="flex flex-wrap justify-between gap-2">
                <Button name="link" onClick={closeModal} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={!canSave}>
                    {isSaving ? <LoadingDots /> : 'Save'}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default FreeLeadsModal
